import { Component, computed, effect, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CartItemStateService } from '../../../services/cart-item-state.service';
import { CiInputValue } from '../../../model/cart-item';
import { BooktechAppService } from '../../../services/booktech-app.service';
import { BtFormRowComponent } from '../../form/bt-form-row/bt-form-row.component';
import { DynformControl } from '../../../model/dymform-control';
import { LibModulesWolcModule } from '../../lib-modules-wolc.module';

@Component({
  selector: 'btlib-order-input-fields',
  standalone: true,
  imports: [BtFormRowComponent, LibModulesWolcModule],
  templateUrl: './order-input-fields.component.html',
  styleUrl: './order-input-fields.component.scss'
})
export class BtOrderInputFieldsComponent {
  state = inject(CartItemStateService);
  bas = inject(BooktechAppService);

  values = input<CiInputValue[] | null>(null);
  @Output() valueChange = new EventEmitter<CiInputValue>();

  inputValues = computed(() => this.values() ?? this.state.inputValues());
  hasInputValues = computed(() => (this.inputValues() ?? []).length > 0);

  form = signal<{ form: UntypedFormGroup | null; controls: DynformControl[] }>({ form: null, controls: [] });

  constructor() {
    effect(() => this.buildForm(this.inputValues()));
  }

  private buildForm(values: CiInputValue[]): void {
    const controls: DynformControl[] = [];

    for (const iv of values) {
      const input = iv.input;
      const key = this.controlKey(iv);

      const type = (input.type || '').toLowerCase();
      const isCheckbox = type.indexOf('checkbox') >= 0;
      const isTextArea = type.indexOf('textarea') >= 0;
      const isSelect = type.indexOf('select') >= 0;

      let controlType = 'input';
      let value: any = iv.value ?? input.defaultValue ?? '';
      let options: (() => any[]) = () => [];

      if (isCheckbox) {
        controlType = 'checkbox';
        value = value === true || value === 'true';
      } else if (isTextArea) {
        controlType = 'textarea';
      } else if (isSelect) {
        controlType = 'select';
        const opts = (iv.availableOptions ?? input.options?.split(',') ?? [])
          .map(o => (typeof o === 'string' ? o.trim() : o))
          .filter(o => o !== '')
          .map(o => ({ value: o, label: o }));
        options = () => opts;
        if (!value && opts.length > 0) {
          value = opts[0].value;
        }
      }

      const ctl = new DynformControl({
        key,
        valuePath: key,
        label: input.mkName || input.name || '',
        mk: input.mkName || '',
        required: !!input.required,
        controlType,
        type: isCheckbox ? 'checkbox' : 'text',
        options,
        value,
        data: { inputValue: iv }
      });

      ctl.onChange = (event: any) => {
        const changed = event?.control?.data?.inputValue as CiInputValue;
        if (!changed) return;
        const updated = { ...changed, value: event.control.value };
        if (this.values() != null) {
          this.valueChange.emit(updated);
        } else {
          this.state.updateInputValue(updated);
        }
      };

      controls.push(ctl);
    }

    const formObj: any = {};
    this.bas.fs.updateFormObj(formObj, controls, {});
    this.form.set({ form: formObj.form, controls });
  }

  private controlKey(iv: CiInputValue): string {
    return `input_${iv.input.id}_${iv.index ?? 0}_${iv.guestIndex ?? 0}`;
  }

  markAllTouched(): boolean {
    const fg = this.form().form;
    if (!fg) return true;
    Object.values(fg.controls).forEach(c => {
      c.markAsTouched();
      c.markAsDirty();
      c.updateValueAndValidity();
    });
    return fg.valid;
  }
}
